import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaTasks,
  FaUserTie,
  FaUsers,
  FaFileAlt,
  FaClipboardList,
  FaFilePdf,
  FaFileExcel,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import API_BASE_URL from "../config";


const AdminReports = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  // Reports definition
  const reports = [
    { id: "tasks", name: "Task Performance", api: "/api/reports/tasks", icon: <FaTasks size={36} className="text-primary" /> },
    { id: "employees", name: "Employees", api: "/api/reports/employees", icon: <FaUserTie size={36} className="text-success" /> },
    { id: "users", name: "Users", api: "/api/reports/users", icon: <FaUsers size={36} className="text-info" /> },
    { id: "documents", name: "Documents", api: "/api/reports/documents", icon: <FaFileAlt size={36} className="text-warning" /> },
    { id: "leaves", name: "Leave Requests", api: "/api/reports/leaves", icon: <FaClipboardList size={36} className="text-secondary" /> },
    { id: "performance", name: "Performance", api: "/api/reports/performance", icon: <FaTasks size={36} className="text-dark" /> },
    { id: "leavePlans", name: "Leave Plans", api: "/api/reports/leave-plans", icon: <FaClipboardList size={36} className="text-muted" /> },
  ];


  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // Load bootstrap dynamically
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    link.id = "adminreports-bootstrap";
    document.head.appendChild(link);
    return () => {
      const existingLink = document.getElementById("adminreports-bootstrap");
      if (existingLink) existingLink.remove();
    };
  }, []);

  // Build query params based on filters
  const buildQueryParams = (format) => {
    let params = `?format=${format}&type=${filterType}`;
    if (filterType === "date") {
      if (fromDate) params += `&fromDate=${fromDate}`;
      if (toDate) params += `&toDate=${toDate}`;
    } else if (filterType === "month") {
      if (month) {
        const [y, m] = month.split("-");
        params += `&month=${parseInt(m)}&year=${parseInt(y)}`;
      }
    } else if (filterType === "year") {
      if (year) params += `&year=${year}`;
    }
    return params;
  };

  // Download report
  const downloadReport = async (report, format) => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams(format);
      const res = await fetch(`${API_BASE_URL}${report.api}${queryParams}`, {
        method: "GET",
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) throw new Error("Failed to download report");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${report.id}_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert("⚠️ Error downloading report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-5">
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="fw-bold display-6">📊 Admin Reports</h2>
        <p className="text-muted">Generate professional reports in PDF or Excel format with filters.</p>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-4">
        <div
          className="card-header d-flex justify-content-between align-items-center bg-light"
          style={{ cursor: "pointer" }}
          onClick={() => setShowFilters(!showFilters)}
        >
          <span className="fw-semibold"><FaFilter className="me-2" /> Filters</span>
          {showFilters ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        {showFilters && (
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Filter Type</label>
                <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="all">All</option>
                  <option value="date">By Date</option>
                  <option value="month">By Month</option>
                  <option value="year">By Year</option>
                </select>
              </div>

              {filterType === "date" && (
                <>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">From</label>
                    <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">To</label>
                    <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                </>
              )}

              {filterType === "month" && (
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Month</label>
                  <input type="month" className="form-control" value={month} onChange={(e) => setMonth(e.target.value)} />
                </div>
              )}

              {filterType === "year" && (
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Year</label>
                  <input type="number" className="form-control" placeholder="e.g., 2025" value={year} onChange={(e) => setYear(e.target.value)} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center mb-3">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="fw-semibold mt-2">Generating Report...</p>
        </div>
      )}

      {/* Report Cards */}
      <div className="row g-4">
        {reports.map((report) => (
          <div key={report.id} className="col-md-4 col-lg-3">
            <div className="card h-100 shadow-sm border-0 text-center p-3 hover-shadow">
              <div className="mb-3">{report.icon}</div>
              <h6 className="fw-bold mb-3">{report.name}</h6>
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                  onClick={() => downloadReport(report, "pdf")}
                  title="Download as PDF"
                >
                  <FaFilePdf /> PDF
                </button>
                <button
                  className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                  onClick={() => downloadReport(report, "xlsx")}
                  title="Download as Excel"
                >
                  <FaFileExcel /> Excel
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Back Button
      <div className="text-center mt-5">
        <button className="btn btn-outline-secondary px-4 py-2" onClick={() => navigate(-1)}>⬅ Back</button>
      </div> */}
    </div>
  );
};

export default AdminReports;
